import React, { useContext, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import {
  ActionPlanCollaboratorsSchema,
  ActionPlanTaskSchema,
  ActionPlanTasksSchema,
} from '../../definitions/schemas';
import { ActionPlanContext } from './ActionPlanContext';
import { ActionPlanTaskStatus } from '../../enums/ActionPlans';
import { capitalize } from '../../lib/String';
import { Event as AnalyticsEvent, Events } from '../../enums/Analytics';
import { getTaskBaseProperties } from './helper';
import { isTaskCompleted } from './Tasks/helper';
import { useI18n } from '../../lib/hook/useI18n';
import { useShowConfirm } from '../../lib/hook/useShowConfirm';
import ActionPlanTasksInterface, {
  CreateActionPlanTaskInput,
  UpdateActionPlanTaskInput,
} from '../../graphql/ActionPlans/ActionPlanTasksInterface';
import Analytics from '../../tools/Analytics';
import CompleteTaskConfirmDialog from './Tasks/CompleteTaskConfirmDialog';
import ConflictResolutionDialog from './Tasks/ConflictResolutionDialog';
import SnackbarStore from '../../stores/observables/SnackbarStore';
import UserStore from '../../stores/observables/UserStore';

export interface ActionPlanControllerApi {
  onCreateTask: (payload: CreateActionPlanTaskInput) => Promise<void>;
  onDeleteTask: (id?: string, name?: string) => Promise<boolean>;
  onTaskDeleted: (id: string) => void;
  onTaskReordered: (
    id: string,
    previousTaskId: string | undefined,
    orderedTasks: ActionPlanTasksSchema
  ) => Promise<void>;
  onUpdateTask: (payload: UpdateActionPlanTaskInput) => Promise<void>;
  onUpdateTaskStatus: (
    task: ActionPlanTaskSchema,
    status: ActionPlanTaskStatus
  ) => Promise<void>;
  savingId: string;
}

type PickedApiProps = Pick<
  ActionPlanControllerApi,
  'onTaskDeleted' | 'onTaskReordered'
>;

interface Props extends PickedApiProps {
  actionPlanId?: string;
  children: (controller: ActionPlanControllerApi) => React.ReactNode;
  displayFeedbackImmediately?: boolean;
  onTaskPointerConflict: () => Promise<void>;
  onTaskSaved: (
    task: ActionPlanTaskSchema,
    overwriteSubtasks?: boolean
  ) => void;
  referrer: string;
  taskOwnersOptions: ActionPlanCollaboratorsSchema;
  userContactId?: string;
}

const ActionPlanController: React.FC<Props> = (props: Props): JSX.Element => {
  const { setDirty } = useContext(ActionPlanContext);

  const { t } = useI18n();
  const showConfirm = useShowConfirm();

  const {
    actionPlanId,
    children,
    displayFeedbackImmediately = false,
    onTaskDeleted,
    onTaskPointerConflict,
    onTaskReordered,
    onTaskSaved,
    referrer,
    taskOwnersOptions,
    userContactId,
  } = props;

  const [savingId, setSavingId] = useState('');

  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictingPayload, setConflictingPayload] =
    useState<UpdateActionPlanTaskInput>();

  const [confirmSubtasksCompletion, setConfirmSubtasksCompletion] =
    useState(false);

  const taskToComplete = useRef<ActionPlanTaskSchema | undefined>();

  const isAuthenticatedUser = !!UserStore.get();

  const saveTask = async (
    id: string,
    action: string,
    saveFn: () => Promise<ActionPlanTaskSchema>,
    onConflict?: () => void,
    overwriteSubtasks = false
  ): Promise<void> => {
    try {
      setSavingId(id);

      const task = await saveFn();

      onTaskSaved(task, overwriteSubtasks);
      setDirty(false);

      if (displayFeedbackImmediately) {
        SnackbarStore.show(t(`map.task.action.success.${action}`));
      }

      setSavingId('');
    } catch (e) {
      // make sure this error matches the one from the backend
      // @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/contact/mutations/action_plans/tasks/update_action_plan_task.rb#L20
      const isConflictError =
        typeof e === 'string' && e === 'task already has a newer revision';

      if (isConflictError && onConflict) {
        onConflict();
      } else {
        SnackbarStore.show(capitalize(`${e}`));
        setSavingId('');
      }
    }
  };

  const resetConflict = () => {
    setSavingId('');
    setConflictDialogOpen(false);
    setConflictingPayload(undefined);
  };

  const onConflict = (payload: UpdateActionPlanTaskInput) => {
    setConflictingPayload(payload);
    setConflictDialogOpen(true);
  };

  const onDiscardConflict = (latestTaskVersion: ActionPlanTaskSchema) => {
    onTaskSaved(latestTaskVersion);
    resetConflict();
  };

  const onOverwriteConflict = async (payload: UpdateActionPlanTaskInput) => {
    await onUpdateTask(payload);
    resetConflict();
  };

  const onCreateTask = async (payload: CreateActionPlanTaskInput) =>
    saveTask('save-task', 'created', async () => {
      const response = await new ActionPlanTasksInterface(userContactId).create(
        payload
      );

      const { task, taskPointerConflict } = response.createActionPlanTask;

      trackTaskEvent(
        task,
        isAuthenticatedUser
          ? Events.MAP_TASK_USER_CREATED
          : Events.MAP_TASK_CONTACT_CREATED
      );

      if (taskPointerConflict) {
        onTaskPointerConflict();
      }

      return task;
    });

  const onUpdateTask = async (
    payload: UpdateActionPlanTaskInput,
    subtasks?: ActionPlanTasksSchema
  ) =>
    saveTask(
      payload.id,
      'updated',
      async () => {
        const response = await new ActionPlanTasksInterface(
          userContactId
        ).update(payload);

        const updatedTask = response.updateActionPlanTask.task;

        trackTaskEvent(
          updatedTask,
          isAuthenticatedUser
            ? Events.MAP_TASK_USER_EDITED
            : Events.MAP_TASK_CONTACT_EDITED
        );

        return { ...updatedTask, subtasks };
      },
      () => onConflict(payload),
      payload.completePendingSubtasks
    );

  const handleTaskStatusChange = (
    id: string,
    status: ActionPlanTaskStatus,
    subtasks?: ActionPlanTasksSchema,
    completePendingSubtasks = false
  ) => {
    // if the subtasks were completed automatically, overwrite the in-memory state
    const shouldOverwriteSubtasks = completePendingSubtasks;

    return saveTask(
      id,
      'status_updated',
      async () => {
        const response = await new ActionPlanTasksInterface(
          userContactId
        ).updateStatus({ completePendingSubtasks, id, status });

        const updatedTask = response.updateActionPlanTaskStatus.task;

        trackTaskEvent(
          updatedTask,
          isAuthenticatedUser
            ? Events.MAP_TASK_USER_STATUS_EDITED
            : Events.MAP_TASK_CONTACT_STATUS_EDITED
        );

        return { ...updatedTask, subtasks };
      },
      undefined,
      shouldOverwriteSubtasks
    );
  };

  const onTaskCompleted = (
    completePendingSubtasks: boolean,
    subtasks: ActionPlanTasksSchema
  ) => {
    if (!taskToComplete.current) {
      return;
    }

    handleTaskStatusChange(
      taskToComplete.current.id,
      ActionPlanTaskStatus.Completed,
      subtasks,
      completePendingSubtasks
    );

    taskToComplete.current = undefined;
    setConfirmSubtasksCompletion(false);
  };

  const onUpdateTaskStatus = async (
    task: ActionPlanTaskSchema,
    status: ActionPlanTaskStatus
  ) => {
    const pendingSubtasks = (task.subtasks || []).filter(
      (subtask) => !isTaskCompleted(subtask)
    );

    if (
      pendingSubtasks.length === 0 ||
      status === ActionPlanTaskStatus.Pending
    ) {
      handleTaskStatusChange(task.id, status);
      return;
    }

    taskToComplete.current = task;
    setConfirmSubtasksCompletion(true);
  };

  const handleDeleteTask = async (id?: string) => {
    try {
      if (!id) {
        return false;
      }

      setSavingId(id);

      await new ActionPlanTasksInterface(userContactId).delete({ id });

      onTaskDeleted(id);
      setDirty(false);

      if (displayFeedbackImmediately) {
        SnackbarStore.show(t('map.task.delete.modal.success'));
      }

      return true;
    } catch (e) {
      SnackbarStore.show(`${e}`);
      return false;
    } finally {
      setSavingId('');
    }
  };

  const onDeleteTask = async (id?: string, name?: string): Promise<boolean> =>
    showConfirm({
      cancellationText: t('map.task.delete.modal.cancel'),
      confirmationText: t('map.task.delete.modal.delete'),
      disablePortal: true,
      title: t('map.task.delete.modal.title'),
      useErrorColor: true,
      description: (
        <Typography color="textSecondary" component="span">
          {t('map.task.delete.modal.body', { name })}
        </Typography>
      ),
    }).then(
      () => handleDeleteTask(id),
      // return false, meaning the task wasn't deleted because the confirmation
      // was cancelled
      () => false
    );

  const trackTaskEvent = (
    task: ActionPlanTaskSchema,
    event: AnalyticsEvent
  ) => {
    Analytics.track(event, getTaskBaseProperties(task, referrer));
  };

  const onCancelCompleteTask = () => {
    setConfirmSubtasksCompletion(false);
    taskToComplete.current = undefined;
  };

  return (
    <>
      {children({
        onCreateTask,
        onDeleteTask,
        onTaskDeleted,
        onTaskReordered,
        onUpdateTask,
        onUpdateTaskStatus,
        savingId,
      })}

      {conflictingPayload && (
        <ConflictResolutionDialog
          actionPlanId={actionPlanId}
          isAuthenticatedUser={isAuthenticatedUser}
          onDiscard={onDiscardConflict}
          onOverwrite={onOverwriteConflict}
          open={conflictDialogOpen}
          payload={conflictingPayload}
          referrer={referrer}
          taskOwnersOptions={taskOwnersOptions}
          userContactId={userContactId}
        />
      )}

      {confirmSubtasksCompletion && taskToComplete.current && (
        <CompleteTaskConfirmDialog
          onClose={onCancelCompleteTask}
          onComplete={onTaskCompleted}
          subtasks={taskToComplete.current.subtasks || []}
        />
      )}
    </>
  );
};

export default ActionPlanController;
